import { type Action } from 'redux';
import { type ThunkDispatch } from 'redux-thunk';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

export const LOCAL_STORAGE_CURRENT_EMOJI_KEY = 'currentEmojiId';
const visualizerLink = config.visualizerLink;

import { useAppDispatch } from '@pkg/hooks';
import { LocalStorageKeys } from '@pkg/types';

import {
    apiFactory,
    apiReturnResults,
    type EmojiId,
    Emojis,
    type EmojiVisualizerFiles,
    type EmojiVisualizerIsLocked,
    type EmojiVisualizerMetadata,
    type EmojiVisualizerNameIsLocked,
    type ExtendedEmoji,
    type SearchResponse,
    type Tag,
} from '@app/client';
import config from '@app/config';
import { checkYatLength } from '@app/helpers';
// eslint-disable-next-line import/no-cycle
import { getDashboard } from '@app/store/actions/dashboard';
import { getAllVisualizerData } from '@app/store/actions/emoji';
import { setError } from '@app/store/actions/error';
// eslint-disable-next-line import/no-cycle
import { loadCurrentEmojiOpenSeaUrl } from '@app/store/actions/nft';
import { type RootState } from '@app/store/deferred-redux-store';

import { useMintingOverlayStore } from '../overlays/useMintingOverlayStore';

const FREE_YAT_LENGTH = 6;

type StoreState = {
    blockedUntil: string | null;
    currentEmoji: EmojiId;
    currentEmojiChainFormat: EmojiId | null;
    currentEmojiMinted: boolean;
    currentEmojiTokenId: number | null;
    currentEmojiSearchResponse: SearchResponse | null;
    currentRhythmScore: number;
    currentGeneration: number;
    editingTags: Tag[];
    emojis: EmojiId[];
    extendedEmojis: ExtendedEmoji[];
    loadingEmojis: boolean;
    loadingEmojiTags: boolean;
    tags: Tag[];
    visualizerFiles: EmojiVisualizerFiles | null;
    visualizerName: string | null;
    visualizerMetadata: EmojiVisualizerMetadata | null;
    visualizerUrl: string | null;
    visualizerIsLocked: EmojiVisualizerIsLocked;
    visualizerNameIsLocked: EmojiVisualizerNameIsLocked;
    removedEmojis: EmojiId[]; // Emojis transferred out from this account
    currentFree: boolean;
    loadingVisualized: boolean;
};

type StoreTypes = StoreState & {
    setCurrentEmoji: (emoji: EmojiId) => void;
    setEditingTags: (tags: Tag[]) => void;
    setEmojis: (emojis: EmojiId[]) => void;
    setLoadingEmojis: (loadingEmojis: boolean) => void;
    setLoadingEmojiTags: (loadingEmojiTags: boolean) => void;
    setTags: (tags: Tag[]) => void;
    setVisualizerFiles: (files: EmojiVisualizerFiles | null) => void;
    setVisualizerName: (name: string | null, isLocked?: boolean) => void;
    setVisualizerMetadata: (metadata: EmojiVisualizerMetadata, isLocked?: boolean | null) => void;
    setRemovedEmojis: (params: EmojiId[]) => void;
    setLoadingVisualized: (loadingVisualized: boolean) => void;
};

function getCurrentEmojiData(currentEmojiId: EmojiId, extendedEmojis: ExtendedEmoji[]) {
    const data = extendedEmojis.find((e) => e.display_format === currentEmojiId) || ({} as ExtendedEmoji);
    return {
        currentRhythmScore: data.rhythm_score ?? 0,
        blockedUntil: data.blocked_until ?? null,
        currentEmojiChainFormat: data.chain_format ?? null,
        currentEmojiMinted: data.minted ?? false,
        currentEmojiTokenId: data.token_id ?? null,
        currentGeneration: data.generation || 0,
        currentFree: checkIsFreeYat(data.display_format),
    };
}

const initialState = {
    emojis: [],
    extendedEmojis: [],
    currentEmoji: localStorage.getItem(LOCAL_STORAGE_CURRENT_EMOJI_KEY) || '',
    currentEmojiChainFormat: null,
    currentEmojiMinted: false,
    currentEmojiSearchResponse: null,
    currentEmojiTokenId: null,
    currentFree: false,
    currentRhythmScore: 0,
    currentGeneration: 0,
    tags: [],
    editingTags: [],
    loadingVisualized: false,
    loadingEmojis: false,
    loadingEmojiTags: false,
    blockedUntil: null,
    visualizerFiles: null,
    visualizerName: null,
    visualizerMetadata: null,
    visualizerUrl: '',
    visualizerIsLocked: null,
    visualizerNameIsLocked: null,
    removedEmojis: [],
};

export const useEmojisStore = create<StoreTypes>()(
    devtools(
        (set) => ({
            ...initialState,
            setCurrentEmoji: (currentEmoji) =>
                set((currentState) => {
                    localStorage.setItem(LOCAL_STORAGE_CURRENT_EMOJI_KEY, currentEmoji);
                    const parsedData = getCurrentEmojiData(currentEmoji, currentState.extendedEmojis);
                    const visualizerUrl = `${visualizerLink}?YAT=${currentEmoji}`;
                    return {
                        ...parsedData,
                        currentEmoji,
                        tags: [],
                        visualizerUrl,
                        visualizerFiles: null,
                        visualizerName: null,
                        visualizerMetadata: null,
                    };
                }),
            setLoadingVisualized: (loadingVisualized) => set({ loadingVisualized }),
            setEditingTags: (editingTags) => set({ editingTags }),
            setEmojis: (emojis) => set({ emojis }),
            setLoadingEmojis: (loadingEmojis) => set({ loadingEmojis }),
            setLoadingEmojiTags: (loadingEmojiTags) => set({ loadingEmojiTags }),
            setTags: (tags) => set({ tags }),
            setVisualizerFiles: (visualizerFiles) => set({ visualizerFiles }),
            setVisualizerName: (visualizerName, visualizerIsLocked) =>
                set(() => {
                    return { visualizerName, visualizerIsLocked };
                }),
            setVisualizerMetadata: (visualizerMetadata, visualizerIsLocked) =>
                set(() => {
                    return { visualizerMetadata, visualizerIsLocked };
                }),
            setRemovedEmojis: (removedEmojis) => set({ removedEmojis }),
        }),
        { name: 'emojis-store' },
    ),
);

export const resetUserEmojis = () => {
    useEmojisStore.setState((current) => ({ ...current, ...initialState }));
};

export const handleSearchEmojis = (eid: EmojiId, signal?: AbortSignal) => {
    return apiReturnResults<SearchResponse>(Emojis.Emoji.search, {
        query: { emoji_id: eid },
        settings: { signal },
    });
};

const searchEmojis = async (eid: EmojiId, signal?: AbortSignal) => {
    return handleSearchEmojis(eid, signal)
        .then((r) => {
            if (r?.ok && r.value) {
                useEmojisStore.setState((currentState) => ({ ...currentState, currentEmojiSearchResponse: r.value }));
            }
        })
        .catch((e) => {
            setError({
                ...e,
                log: true,
                source: 'Error searching emojis',
            })();
            return e;
        });
};

export const handleSetActiveEmoji = async (emoji: EmojiId, dispatch: ThunkDispatch<RootState, void, Action>) => {
    useMintingOverlayStore.getState().reset();
    const { setCurrentEmoji, setLoadingVisualized, setVisualizerMetadata, setVisualizerFiles, setVisualizerName } =
        useEmojisStore.getState();

    setLoadingVisualized(true);
    setCurrentEmoji(emoji);
    await searchEmojis(emoji);

    // Set Visualizer & OpenSea URL
    await dispatch(loadCurrentEmojiOpenSeaUrl());
    // Load visualizer info

    const r = await getAllVisualizerData(emoji);
    if (r?.ok && r?.value) {
        const data = r.value;

        const {
            VisualizerData = { data: null, is_locked: false },
            VisualizerFileLocations = { data: null, is_locked: false },
            VisualizerName = { data: { name: null }, is_locked: false },
        } = data;

        if (VisualizerData && VisualizerData.data && Object.keys(VisualizerData.data).length > 0) {
            setVisualizerMetadata(VisualizerData.data, VisualizerData.is_locked);
        }
        if (VisualizerFileLocations && VisualizerFileLocations.data) {
            setVisualizerFiles({
                image: VisualizerFileLocations.data.image,
                video: VisualizerFileLocations.data.video,
            });
        }
        if (VisualizerName) {
            setVisualizerName(VisualizerName.data.name, VisualizerName.is_locked);
        }
    }
    setLoadingVisualized(false);
};
export const useSetUpdateActiveEmoji = () => {
    const dispatch = useAppDispatch();
    return async (emoji: EmojiId) => handleSetActiveEmoji(emoji, dispatch);
};

type UpdateEmojiStoreStateProps = {
    extendedEmojis: StoreState['extendedEmojis'];
    dispatch: ThunkDispatch<RootState, void, Action>;
};
export const updateUserExtendedEmojis = ({ extendedEmojis, dispatch }: UpdateEmojiStoreStateProps) => {
    let currentEmoji = useEmojisStore.getState().currentEmoji;
    const validCurrentEmoji = currentEmoji && extendedEmojis.some((emoji) => emoji.display_format === currentEmoji);
    if (!validCurrentEmoji) {
        currentEmoji = extendedEmojis?.[0].display_format;
        localStorage.setItem(LOCAL_STORAGE_CURRENT_EMOJI_KEY, currentEmoji);
    }

    useEmojisStore.setState((current) => ({
        ...current,
        extendedEmojis,
        emojis: extendedEmojis.map((e) => e.display_format),
    }));

    handleSetActiveEmoji(currentEmoji, dispatch);
};

export function notifyEmojisRemoved(emojis?: ExtendedEmoji[]) {
    // Get previous list of emoji ids
    const previousEmojiIdsSerialized = localStorage.getItem(LocalStorageKeys.EmojiIds);
    let previousEmojiIds = [];
    if (previousEmojiIdsSerialized) {
        previousEmojiIds = JSON.parse(previousEmojiIdsSerialized);
    }

    // Fetch new list of emoji ids
    const extendedEmojis = emojis || useEmojisStore.getState().extendedEmojis;
    let emojiList: string[] = [];
    if (extendedEmojis) {
        emojiList = extendedEmojis.map((emoji) => emoji.canonical_format);
    }

    // Calculate emojis that were removed from the account
    const difference = previousEmojiIds.filter((x) => !emojiList.includes(x));

    // If Yats removed
    if (difference.length > 0) {
        useEmojisStore.setState((current) => ({ ...current, removedEmojis: difference }));
    }

    // Save new list
    localStorage.setItem(LocalStorageKeys.EmojiIds, JSON.stringify(emojiList));
}

export async function getCurrentUserEmojis(dispatch: ThunkDispatch<RootState, void, Action>, showLoader = true) {
    try {
        if (showLoader) {
            useEmojisStore.setState((current) => ({ ...current, loadingEmojis: true }));
        }
        const r = await apiFactory().call(Emojis.Emoji.getExtendedCollection, {});
        if (r.ok) {
            updateUserExtendedEmojis({ extendedEmojis: r.value, dispatch });
            await dispatch(getDashboard());
            notifyEmojisRemoved();
        } else {
            dispatch(setError({ ...r, source: 'UserEmojis - getCurrentUserEmojis()' }));
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
        dispatch(setError({ ...e, source: 'UserEmojis - getCurrentUserEmojis() - catch' }));
    } finally {
        if (showLoader) {
            useEmojisStore.setState((current) => ({ ...current, loadingEmojis: true }));
        }
    }
    return true;
}

export function checkIsFreeYat(eid: string) {
    return checkYatLength(eid) === FREE_YAT_LENGTH;
}
